import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AlertTriangle, X, ArrowRight, Sparkles, Zap } from "lucide-react";

/**
 * Global trial-status banner shown on every authenticated page.
 *
 * Urgency escalates as trial nears its end:
 *   - > 3 days:   NOT shown (silent)
 *   - 3-2 days:   Amber, dismissible
 *   - 1 day:      Red, pulsing, dismissible
 *   - Expired:    Red permanent (cannot dismiss — nudges to pay)
 *
 * Also handles paid users whose ``paid_until`` is in the past.
 */
export default function TrialBanner() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(
    () => localStorage.getItem("vyntric.trial_banner_dismissed_days") || ""
  );

  useEffect(() => {
    if (!user) return;
    // Platform admins on lifetime account never need to see this
    if (user.role === "platform_admin") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/subscription");
        if (!cancelled) setSub(data);
      } catch (_e) {
        /* silent — dashboard also uses this endpoint */
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!sub) return null;
  if (sub.is_lifetime) return null;

  // Compute days remaining. Only alerts when ≤ 3 days OR expired.
  const now = new Date();
  let daysLeft = null;
  let expired = false;

  if (sub.trial_active && sub.trial_ends_at) {
    const t = new Date(sub.trial_ends_at);
    daysLeft = Math.ceil((t - now) / 86400000);
  } else if (!sub.can_use) {
    expired = true;
  } else if (sub.is_paid && sub.paid_until) {
    const p = new Date(sub.paid_until);
    const remaining = Math.ceil((p - now) / 86400000);
    if (remaining <= 3 && remaining >= 0) daysLeft = remaining;
    else if (remaining < 0) expired = true;
  }

  // If not urgent, hide.
  if (!expired && (daysLeft === null || daysLeft > 3)) return null;

  const dismissKey = expired ? "expired" : `d${daysLeft}`;
  if (!expired && dismissedFor === dismissKey) return null;

  const dismiss = () => {
    localStorage.setItem("vyntric.trial_banner_dismissed_days", dismissKey);
    setDismissedFor(dismissKey);
  };

  // ---------- Expired: BLOCKING red banner ----------
  if (expired) {
    return (
      <div
        className="border-b-2 border-red-500 bg-gradient-to-r from-red-600 to-red-700 text-white"
        data-testid="trial-banner-expired"
      >
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black uppercase tracking-wider">
              Sua assinatura expirou
            </div>
            <div className="text-xs text-red-100 mt-0.5">
              Regularize seu pagamento para voltar a usar o Vyntric. Os pedidos
              não estão sendo faturados automaticamente enquanto a conta estiver expirada.
            </div>
          </div>
          <Link
            to="/minha-conta?tab=billing"
            data-testid="trial-banner-cta-expired"
            className="flex-shrink-0 px-4 py-2 text-xs font-black uppercase tracking-wider bg-white text-red-700 hover:bg-red-50 inline-flex items-center gap-2 rounded-sm shadow-md"
          >
            Regularizar agora <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  // ---------- Urgent (≤ 3 days): amber → red as it gets closer ----------
  const isCritical = daysLeft <= 1;
  const styles = isCritical
    ? "border-red-400 bg-gradient-to-r from-red-50 to-red-100"
    : "border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100";
  const iconColor = isCritical ? "text-red-700" : "text-amber-700";
  const titleColor = isCritical ? "text-red-900" : "text-amber-900";
  const subColor = isCritical ? "text-red-800" : "text-amber-800";
  const ctaBg = isCritical
    ? "bg-red-600 hover:bg-red-700 text-white"
    : "bg-amber-600 hover:bg-amber-700 text-white";
  const dotColor = isCritical ? "bg-red-500" : "bg-amber-500";

  const dayLabel = daysLeft === 1 ? "1 dia" : `${daysLeft} dias`;
  const titleText = sub.trial_active
    ? `Seu trial termina em ${dayLabel}`
    : `Sua assinatura vence em ${dayLabel}`;
  const subText = sub.trial_active
    ? `Após o trial, sua conta será cobrada em R$ ${sub.monthly_total_brl.toFixed(2).replace(".", ",")}/mês. Assine antes para não perder o acesso.`
    : `Regularize seu pagamento para continuar usando o Vyntric sem interrupção.`;

  return (
    <div
      className={`border-b-2 ${styles}`}
      data-testid="trial-banner-warning"
    >
      <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
        <div className={`relative flex-shrink-0 ${isCritical ? "animate-pulse" : ""}`}>
          <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
          <span className={`absolute inset-0 rounded-full ${dotColor} opacity-40 animate-ping`} />
        </div>
        {sub.trial_active ? (
          <Sparkles className={`w-5 h-5 ${iconColor}`} />
        ) : (
          <Zap className={`w-5 h-5 ${iconColor}`} />
        )}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-black uppercase tracking-wider ${titleColor}`}>
            {titleText}
          </div>
          <div className={`text-xs mt-0.5 ${subColor}`}>
            {subText}
          </div>
        </div>
        <Link
          to="/minha-conta?tab=billing"
          data-testid="trial-banner-cta-warning"
          className={`flex-shrink-0 px-4 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 rounded-sm shadow-md ${ctaBg}`}
        >
          {sub.trial_active ? "Assinar agora" : "Renovar"} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <button
          data-testid="trial-banner-dismiss"
          onClick={dismiss}
          className={`flex-shrink-0 p-1.5 ${iconColor} hover:bg-black/5 rounded-sm`}
          title="Dispensar por hoje"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
